import { Fragment, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { Commit } from '@mui/icons-material';
import type { GetMyProjectActivityFeedQueryData } from '@/generated/graphql';
import { formatUtcDate } from './activityCalendar';

type ActivityItem =
  GetMyProjectActivityFeedQueryData['myProjectActivityFeed']['items'][number];

interface ProjectActivityFeedProps {
  items: ActivityItem[];
  loading: boolean;
  error?: Error;
  hasMore: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
}

function monthKey(value: string) {
  return value.slice(0, 7);
}

function monthLabel(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value));
}

export default function ProjectActivityFeed({
  items,
  loading,
  error,
  hasMore,
  onLoadMore,
  onRetry,
}: ProjectActivityFeedProps) {
  const groups = useMemo(() => {
    const grouped = new Map<string, ActivityItem[]>();
    items.forEach((item) => {
      const key = monthKey(item.occurredAt);
      grouped.set(key, [...(grouped.get(key) ?? []), item]);
    });
    return [...grouped.entries()];
  }, [items]);

  if (error && items.length === 0) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" onClick={onRetry}>
            Retry
          </Button>
        }
      >
        Could not load project activity.
      </Alert>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        Publish a project version to start your activity.
      </Typography>
    );
  }

  return (
    <Stack gap={2}>
      {groups.map(([month, monthItems]) => (
        <Box key={month}>
          <Stack direction="row" alignItems="center" gap={1.5} mb={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              {monthLabel(`${month}-01T00:00:00.000Z`)}
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Stack>
          {monthItems.map((item) => {
            const changed =
              item.version.createdFileCount +
              item.version.updatedFileCount +
              item.version.deletedFileCount;
            return (
              <Fragment key={item.id}>
                <Stack
                  direction="row"
                  gap={1.5}
                  sx={{ py: 1.5 }}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      mt: 0.25,
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'studio.surfaces.fillSubtle',
                      color: 'primary.main',
                      flexShrink: 0,
                    }}
                  >
                    <Commit fontSize="small" />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2">
                      Published version {item.version.number} of{' '}
                      <Link
                        component={RouterLink}
                        to={`/projects/${encodeURIComponent(item.project.id)}?tab=versions`}
                        underline="hover"
                      >
                        {item.project.name}
                      </Link>
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', overflowWrap: 'anywhere' }}
                    >
                      {item.version.commitMessage || 'No commit message'}
                      {changed > 0
                        ? ` · ${changed} changed ${changed === 1 ? 'file' : 'files'}`
                        : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {formatUtcDate(item.occurredAt)}
                  </Typography>
                </Stack>
                <Divider />
              </Fragment>
            );
          })}
        </Box>
      ))}
      {(loading || hasMore) && (
        <Button
          variant="outlined"
          onClick={onLoadMore}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          {loading ? 'Loading activity…' : 'Show more activity'}
        </Button>
      )}
      {error && items.length > 0 && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          Could not load more activity.
        </Alert>
      )}
    </Stack>
  );
}
